import React, { ReactNode, useState, useMemo } from 'react';
import { Page } from './components/PageCard';
import { useWorkflowUnstartedState, useSeeWorkflow } from './components/RsvpPageCard/utils';
import { REGISTRY_PAGE_TYPE } from './helpers/pages';
import useIsLiteSite from './helpers/useIsLiteSite';

interface Props {
  pages: Page[];
  hasAnyRegistry: boolean;
  children: ReactNode | ReactNode[];
}

// PageId should only be a string for the rsvp page card
export type PageId = number | string;

interface Initial {
  isPageOpenObj: any;
  togglePageOpen: (id: PageId) => void;
  setPageOpen: (id: PageId, open: boolean) => void;
  updateAllPageCards: (collapse: boolean) => void;
  allPagesClosed: boolean;
}

interface IsPageOpenObj {
  [pageId: string]: boolean;
}

export const PageCardUIContext = React.createContext<Initial>({} as Initial);

export const PageCardUIProvider: React.FC<Props> = ({ pages, children, hasAnyRegistry }) => {
  const isLiteSite = useIsLiteSite();
  const [eipState, setEipState] = useState(!isLiteSite);

  const [isPageOpenObj, setPageOpenObj] = useState(() => {
    const pageCardStateObj = pages.reduce((obj: IsPageOpenObj, page: Page) => {
      obj[page.id] = isLiteSite;
      return obj;
    }, {});

    // adds a property associated with whether the rsvp page card is open
    pageCardStateObj.rsvp = isLiteSite;

    return pageCardStateObj;
  });
  const { isUnstartedState } = useWorkflowUnstartedState();
  const seeWorkflow = useSeeWorkflow();
  const isRsvpCardVisible = !(isUnstartedState && seeWorkflow);

  const togglePageOpen = (id: PageId) => {
    const open = isPageOpenObj[id];

    setPageOpenObj({
      ...isPageOpenObj,
      [id]: !open,
    });
  };

  const setPageOpen = (id: PageId, open: boolean) => {
    setPageOpenObj({
      ...isPageOpenObj,
      [id]: open,
    });
  };

  const updateAllPageCards = (collapse: boolean) => {
    const newState: IsPageOpenObj = {};

    pages.forEach(page => {
      if (
        (page.type === REGISTRY_PAGE_TYPE && hasAnyRegistry) ||
        (page.type !== REGISTRY_PAGE_TYPE && (page.show || page.items.length))
      ) {
        newState[page.id] = !collapse;
      }
    });

    if (isRsvpCardVisible) {
      newState.rsvp = !collapse;
    }

    setPageOpenObj({ ...isPageOpenObj, ...newState });
  };

  if (eipState === isLiteSite) {
    setEipState(!isLiteSite);
    const newState: IsPageOpenObj = {};

    pages.forEach(page => {
      newState[page.id] = isLiteSite;
    });
    if (isRsvpCardVisible) {
      newState.rsvp = isLiteSite;
    }
    setPageOpenObj({ ...isPageOpenObj, ...newState });
  }

  const allPagesClosed = useMemo(() => {
    const isRsvpCardClosed = !isRsvpCardVisible || (isRsvpCardVisible && !isPageOpenObj.rsvp);

    return (
      isRsvpCardClosed &&
      pages.every(page => {
        if (page.type === REGISTRY_PAGE_TYPE) {
          return !hasAnyRegistry || !isPageOpenObj[page.id];
        } else {
          return (
            (!page.show && page.items.length === 0) ||
            (!page.show && page.items.length > 0 && !isPageOpenObj[page.id]) ||
            (page.show && !isPageOpenObj[page.id])
          );
        }
      })
    );
  }, [isRsvpCardVisible, pages, hasAnyRegistry, isPageOpenObj]);

  return (
    <PageCardUIContext.Provider
      value={{
        isPageOpenObj,
        togglePageOpen,
        setPageOpen,
        updateAllPageCards,
        allPagesClosed,
      }}
    >
      {children}
    </PageCardUIContext.Provider>
  );
};
